import * as dotProp from 'dot-prop-immutable';
import { insert, remove } from 'ramda';
import { Action } from 'typescript-fsa';

import { createReducer, generateUUID } from '@topconagriculture/frontend-utils';

import {
  addIngredientInRecipe,
  addIngredientInRecipeSilent,
  addRecipeTransitionDays,
  changeIngredient,
  deleteCurrentTransionDay,
  deleteIngredientInRecipe,
  editCurrentTransitionDay,
  emptyIngredientsInRecipe,
  previewCreateRecipe,
  recalculateFixedDates,
  setActualLoadTargetWeight,
  setAutoAdjWater,
  setCurrentTransitionDay,
  setDryMatterTargetWeight,
  setEditModeInRecipeCreation,
  setEditRecipe,
  setMethod,
  setSelectedIngredientInList,
  setTableView,
  setTargetWeightType,
  setWaterAmount,
  updateActualLoad,
  updateActualLoadPercentage,
  updateBaseRationActualLoad,
  updateBaseRationActualLoadPercentage,
  updateBaseRationDryMatter,
  updateBaseRationDryMatterPercentage,
  updateBaseRationPerc,
  updateBaseRationRecipe,
  updateDryMatter,
  updateDryMatterPercentage,
  updateIngredientOrderInRecipe,
  updateMixerData,
  updateRecipeTypeId,
  updateRefusalSubstitute,
} from './appRecipes.actions';
import {
  BaseRationData,
  EntryMethodsKeys,
  IngredientInRecipeCreation,
  PreviewRationCreation,
  TargetWeights,
} from './appRecipes.types';
// tslint:disable-next-line
import { Recipe, RecipeType } from '../../entities/recipes/recipes.types';
import { recipeTypeItems } from '../../../config/constants';
import { IngredientInRation } from '../../entities/premixes/premixes.types';

export type AppRecipeState = {
  readonly recipeCreation: {
    readonly ingredientsInRecipe: IngredientInRecipeCreation[];
    readonly previewRecipe: PreviewRationCreation | null;
    readonly isEditMode: boolean;
    readonly autoAdjWater: boolean;
    readonly targetWeightType: TargetWeights;
    readonly method: EntryMethodsKeys;
    readonly targetActualLoadWeight: number[];
    readonly targetDryMatterWeight: number[];
    readonly finalDryMatterPercentage: number;
    readonly selectedIngredientInList: string | null;
    readonly transitionDays: number[];
    readonly currentTransitionDay: number;
    readonly baseRation: BaseRationData;
    readonly recipeTypeId: number;
    readonly tableView: 'grid' | 'graph';
  };
  readonly recipePayload: {
    readonly ingredients: IngredientInRation[];
  };
};

export const initialState: AppRecipeState = {
  recipeCreation: {
    ingredientsInRecipe: [],
    previewRecipe: null,
    isEditMode: false,
    autoAdjWater: false,
    targetWeightType: TargetWeights.DRY,
    method: EntryMethodsKeys.WEIGHT,
    targetActualLoadWeight: [0],
    targetDryMatterWeight: [0],
    finalDryMatterPercentage: 0,
    selectedIngredientInList: null,
    transitionDays: [0],
    currentTransitionDay: 0,
    baseRation: {
      ration: null,
      percentage: 100,
      actualLoad: 0,
      dryMatter: 0,
      actualLoadTotalPercentage: 0,
      dryMatterTotalPercentage: 0,
      ingredients: [],
    },
    recipeTypeId: recipeTypeItems[RecipeType.Default].id,
    tableView: 'grid',
  },
  recipePayload: {
    ingredients: [],
  },
};

const addIngredientInRecipeReducer = (state: AppRecipeState, action: ReturnType<typeof addIngredientInRecipe>) => {
  const existingIngredient = state.recipeCreation.ingredientsInRecipe.find((ing) => ing.id === action.payload.id);
  const ingredientsInRecipe = [...state.recipeCreation.ingredientsInRecipe];
  const { autoAdjWater, currentTransitionDay, transitionDays } = state.recipeCreation;

  if (existingIngredient && action.payload.isOverridable) {
    const overridableItemIndex = state.recipeCreation.ingredientsInRecipe.findIndex(
      (ing) => ing.id === action.payload.id,
    );
    ingredientsInRecipe[overridableItemIndex] = {
      ...existingIngredient,
      actualLoad: existingIngredient.actualLoad.map((elem, index) =>
        index === currentTransitionDay ? elem + action.payload.actualLoad! || 0 : elem,
      ),
    };
  }

  const waterWeight = ingredientsInRecipe.reduce(
    (acc, ing) => acc + (ing.dryMatterPerc === 0 ? ing.actualLoad[currentTransitionDay] : 0),
    0,
  );
  const waterIngredientsCount = ingredientsInRecipe.filter((ing) => ing.dryMatterPerc === 0).length + 1;
  const actualLoad = ingredientsInRecipe.reduce((acc, ing) => acc + ing.actualLoad[currentTransitionDay], 0);
  const isWater = action.payload.dryMatterPerc === 0;

  const { payload } = action;

  const ingredients =
    existingIngredient && action.payload.isOverridable
      ? [...ingredientsInRecipe]
      : [
          // Include ingredient
          ...state.recipeCreation.ingredientsInRecipe.map((elem) => {
            return {
              ...elem,
              actualLoad: [
                ...elem.actualLoad.slice(0, currentTransitionDay),
                elem.dryMatterPerc === 0 && autoAdjWater && isWater
                  ? waterWeight / waterIngredientsCount
                  : elem.actualLoad[currentTransitionDay] || 0,
                ...elem.actualLoad.slice(currentTransitionDay + 1),
              ],
              actualLoadTotalPercentage: [
                ...elem.actualLoadTotalPercentage.slice(0, currentTransitionDay),
                elem.dryMatterPerc === 0 && autoAdjWater && isWater
                  ? (waterWeight / waterIngredientsCount / actualLoad) * 100
                  : elem.actualLoadTotalPercentage[currentTransitionDay],
                ...elem.actualLoadTotalPercentage.slice(currentTransitionDay + 1),
              ],
            };
          }),
          {
            id: action.payload.id,
            ownId: action.payload.ownId,
            uuid: action.payload.listId || generateUUID(),
            actualLoad: new Array(transitionDays.length).fill(
              action.payload.dryMatterPerc === 0 && autoAdjWater
                ? waterWeight / waterIngredientsCount
                : action.payload.actualLoad || 0,
            ),
            dryMatter: new Array(transitionDays.length).fill(action.payload.dryMatter || 0),
            dryMatterPerc: action.payload.dryMatterPerc || 0,
            actualLoadTotalPercentage: new Array(transitionDays.length).fill(
              typeof payload.actualLoadPercentage !== 'undefined'
                ? payload.actualLoadPercentage
                : action.payload.dryMatterPerc === 0 && autoAdjWater
                ? (waterWeight / waterIngredientsCount / actualLoad) * 100
                : 0,
            ),
            dryMatterTotalPercentage: new Array(transitionDays.length).fill(
              typeof payload.dryMatterPercentage !== 'undefined' ? payload.dryMatterPercentage : 0,
            ),
            mixerData: {
              mixingTypeCode: (action.payload.mixerData && action.payload.mixerData.mixingTypeCode) || 0,
              mixingValue: (action.payload.mixerData && action.payload.mixerData.mixingValue) || 0,
              mixingStartTypeCode: (action.payload.mixerData && action.payload.mixerData.mixingStartTypeCode) || 1,
              mixingStartValue: (action.payload.mixerData && action.payload.mixerData.mixingStartValue) || 0,
              mixingRPM: (action.payload.mixerData && action.payload.mixerData.mixingRPM) || 0,
            },
            refusal: action.payload.refusal,
            refusalSubstitute: action.payload.refusalSubstitute,
            toleranceMotion: action.payload.toleranceMotion,
            toleranceWeight: action.payload.toleranceWeight,
          },
        ];

  const isRefusal = isRefusalType(state.recipeCreation.recipeTypeId);
  let ingerdientsResult = [...ingredients];
  if (isRefusal) {
    const refusalIngredients = ingredients.filter((x) => x.refusal);
    const otherIngredients = ingredients.filter((x) => !x.refusal);
    ingerdientsResult = [...refusalIngredients, ...otherIngredients];
  }

  return {
    ...state,
    recipeCreation: {
      ...state.recipeCreation,
      ingredientsInRecipe: ingerdientsResult,
    },
  };
};

const appRecipeReducer = createReducer(initialState, {
  [setEditModeInRecipeCreation.type]: (state: AppRecipeState, action: Action<boolean>) => {
    return dotProp.set(state, 'recipeCreation.isEditMode', action.payload);
  },
  [setAutoAdjWater.type]: (state: AppRecipeState, action: Action<boolean>) => {
    return dotProp.set(state, 'recipeCreation.autoAdjWater', action.payload);
  },
  [setMethod.type]: (state: AppRecipeState, action: Action<EntryMethodsKeys>) => {
    return dotProp.set(state, 'recipeCreation.method', action.payload);
  },
  [setTargetWeightType.type]: (state: AppRecipeState, action: Action<TargetWeights>) => {
    return dotProp.set(state, 'recipeCreation.targetWeightType', action.payload);
  },
  [setCurrentTransitionDay.type]: (state: AppRecipeState, action: Action<number>) => {
    return dotProp.set(state, 'recipeCreation.currentTransitionDay', action.payload);
  },
  [setTableView.type]: (state: AppRecipeState, action: Action<'grid' | 'graph'>) => {
    return dotProp.set(state, 'recipeCreation.tableView', action.payload);
  },
  [recalculateFixedDates.type]: (state: AppRecipeState, action: Action<number>) => {
    const daysDifference = action.payload;

    const { transitionDays } = state.recipeCreation;

    return dotProp.set(
      state,
      'recipeCreation.transitionDays',
      transitionDays.map((day, index) => (index === 0 ? 0 : day - daysDifference)),
    );
  },
  [updateMixerData.type]: (state: AppRecipeState, action: ReturnType<typeof updateMixerData>) => {
    const { payload } = action;
    const { ingredientsInRecipe } = state.recipeCreation;
    const ingredients = ingredientsInRecipe.map((ingredient) => {
      const isCurrentItem = ingredient.uuid === payload.listId;
      return isCurrentItem
        ? {
            ...ingredient,
            mixerData: { ...ingredient.mixerData, ...payload.mixerData },
          }
        : ingredient;
    });
    return dotProp.set(state, 'recipeCreation.ingredientsInRecipe', ingredients);
  },
  [updateActualLoad.type]: (state: AppRecipeState, action: ReturnType<typeof updateActualLoad>) => {
    const { payload } = action;
    const { ingredientsInRecipe, recipeTypeId, baseRation, currentTransitionDay } = state.recipeCreation;
    const isBaseRation = isBaseRationType(recipeTypeId);
    const ingredients = ingredientsInRecipe.map((ingredient) => {
      const isCurrentItem = ingredient.uuid === payload.listId;
      return isCurrentItem
        ? {
            ...ingredient,
            actualLoad: [
              ...ingredient.actualLoad.slice(0, currentTransitionDay),
              payload.actualLoad,
              ...ingredient.actualLoad.slice(currentTransitionDay + 1),
            ],
          }
        : ingredient;
    });
    const newState = {
      recipeCreation: {
        ...state.recipeCreation,
        ingredientsInRecipe: ingredients,
      },
      recipePayload: {
        ...state.recipePayload,
        baseRationAmounts: isBaseRation ? [baseRation.actualLoad] : undefined,
        ingredients: ingredientsInRecipe.map((ingredient) => {
          const isCurrentItem = ingredient.uuid === payload.listId;
          return {
            ...ingredient.mixerData,
            id: ingredient.ownId,
            ingredientId: ingredient.id,
            refusal: ingredient.refusal,
            refusalSubstitute: ingredient.refusalSubstitute,
            amounts: isCurrentItem
              ? [
                  ...ingredient.actualLoad.slice(0, currentTransitionDay),
                  payload.actualLoad,
                  ...ingredient.actualLoad.slice(currentTransitionDay + 1),
                ]
              : ingredient.actualLoad,
          };
        }),
      },
    };
    return newState;
  },
  [updateActualLoadPercentage.type]: (state: AppRecipeState, action: ReturnType<typeof updateActualLoadPercentage>) => {
    const { payload } = action;
    const { ingredientsInRecipe, recipeTypeId, baseRation, targetWeightType, currentTransitionDay } =
      state.recipeCreation;
    const isBaseRation = isBaseRationType(recipeTypeId);
    const ingredients = ingredientsInRecipe.map((ingredient) => {
      const isCurrentItem = ingredient.uuid === payload.listId;
      return isCurrentItem
        ? {
            ...ingredient,
            actualLoadTotalPercentage: [
              ...ingredient.actualLoadTotalPercentage.slice(0, currentTransitionDay),
              payload.actualLoadPercentage,
              ...ingredient.actualLoadTotalPercentage.slice(currentTransitionDay + 1),
            ],
          }
        : ingredient;
    });
    const newState = {
      recipeCreation: {
        ...state.recipeCreation,
        ingredientsInRecipe: ingredients,
      },
      recipePayload: {
        ...state.recipePayload,
        baseRationLoadPercs: isBaseRation ? [baseRation.actualLoadTotalPercentage] : undefined,
        ingredients: ingredientsInRecipe.map((ingredient) => {
          const isCurrentItem = ingredient.uuid === payload.listId;
          const isWater = isWaterIngredient(ingredient.dryMatterPerc);
          const isDryLoadPerc = targetWeightType === TargetWeights.DRY;
          return {
            ...ingredient.mixerData,
            id: ingredient.ownId,
            ingredientId: ingredient.id,
            refusal: ingredient.refusal,
            refusalSubstitute: ingredient.refusalSubstitute,
            dryLoadPercs: !isWater && isDryLoadPerc ? ingredient.dryMatterTotalPercentage : undefined,
            loadPercs: isCurrentItem
              ? [
                  ...ingredient.actualLoadTotalPercentage.slice(0, currentTransitionDay),
                  payload.actualLoadPercentage,
                  ...ingredient.actualLoadTotalPercentage.slice(currentTransitionDay + 1),
                ]
              : ingredient.actualLoadTotalPercentage,
          };
        }),
      },
    };
    return newState;
  },
  [updateDryMatter.type]: (state: AppRecipeState, action: ReturnType<typeof updateDryMatter>) => {
    const { payload } = action;
    const { ingredientsInRecipe, recipeTypeId, baseRation, currentTransitionDay } = state.recipeCreation;
    const isBaseRation = isBaseRationType(recipeTypeId);
    const ingredients = ingredientsInRecipe.map((ingredient) => {
      const isCurrentItem = ingredient.uuid === payload.listId;
      return isCurrentItem
        ? {
            ...ingredient,
            dryMatter: [
              ...ingredient.dryMatter.slice(0, currentTransitionDay),
              payload.dryMatter,
              ...ingredient.dryMatter.slice(currentTransitionDay + 1),
            ],
          }
        : ingredient;
    });
    const newState = {
      recipeCreation: {
        ...state.recipeCreation,
        ingredientsInRecipe: ingredients,
      },
      recipePayload: {
        ...state.recipePayload,
        baseRationDryAmounts: isBaseRation ? [baseRation.dryMatter] : undefined,
        ingredients: ingredientsInRecipe.map((ingredient) => {
          const isCurrentItem = ingredient.uuid === payload.listId;
          const isWater = isWaterIngredient(ingredient.dryMatterPerc);
          return {
            ...ingredient.mixerData,
            id: ingredient.ownId,
            ingredientId: ingredient.id,
            refusal: ingredient.refusal,
            refusalSubstitute: ingredient.refusalSubstitute,
            dryAmounts: isWater
              ? undefined
              : isCurrentItem
              ? [
                  ...ingredient.dryMatter.slice(0, currentTransitionDay),
                  payload.dryMatter,
                  ...ingredient.dryMatter.slice(currentTransitionDay + 1),
                ]
              : ingredient.dryMatter,
            amounts: isWater ? ingredient.actualLoad : undefined,
          };
        }),
      },
    };
    return newState;
  },
  [updateDryMatterPercentage.type]: (state: AppRecipeState, action: ReturnType<typeof updateDryMatterPercentage>) => {
    const { payload } = action;
    const { ingredientsInRecipe, recipeTypeId, baseRation, currentTransitionDay } = state.recipeCreation;
    const isBaseRation = isBaseRationType(recipeTypeId);
    const ingredients = ingredientsInRecipe.map((ingredient) => {
      const isCurrentItem = ingredient.uuid === payload.listId;
      return isCurrentItem
        ? {
            ...ingredient,
            dryMatterTotalPercentage: [
              ...ingredient.dryMatterTotalPercentage.slice(0, currentTransitionDay),
              payload.dryMatterPercentage,
              ...ingredient.dryMatterTotalPercentage.slice(currentTransitionDay + 1),
            ],
          }
        : ingredient;
    });
    const newState = {
      recipeCreation: {
        ...state.recipeCreation,
        ingredientsInRecipe: ingredients,
      },
      recipePayload: {
        ...state.recipePayload,
        baseRationDryLoadPercs: isBaseRation ? [baseRation.dryMatterTotalPercentage] : undefined,
        ingredients: ingredientsInRecipe.map((ingredient) => {
          const isCurrentItem = ingredient.uuid === payload.listId;
          const isWater = isWaterIngredient(ingredient.dryMatterPerc);
          return {
            ...ingredient.mixerData,
            id: ingredient.ownId,
            ingredientId: ingredient.id,
            refusal: ingredient.refusal,
            refusalSubstitute: ingredient.refusalSubstitute,
            dryLoadPercs: isWater
              ? undefined
              : isCurrentItem
              ? [
                  ...ingredient.dryMatterTotalPercentage.slice(0, currentTransitionDay),
                  payload.dryMatterPercentage,
                  ...ingredient.dryMatterTotalPercentage.slice(currentTransitionDay + 1),
                ]
              : ingredient.dryMatterTotalPercentage,
            loadPercs: isWater ? ingredient.actualLoadTotalPercentage : undefined,
          };
        }),
      },
    };
    return newState;
  },
  [updateBaseRationPerc.type]: (state: AppRecipeState, action: ReturnType<typeof updateBaseRationPerc>) => {
    const { ingredientsInRecipe, baseRation, targetActualLoadWeight, targetDryMatterWeight, targetWeightType, method } =
      state.recipeCreation;
    const newState = {
      recipeCreation: {
        ...state.recipeCreation,
        baseRation: {
          ...baseRation,
          percentage: action.payload.perc,
        },
      },
      recipePayload: {
        ...state.recipePayload,
        baseRationPerc: action.payload.perc,
        baseRationId: baseRation.ration ? baseRation.ration.id : undefined,
        amounts:
          method === EntryMethodsKeys.PERCENTAGE && targetWeightType === TargetWeights.ACTUAL
            ? targetActualLoadWeight
            : undefined,
        dryAmounts:
          method === EntryMethodsKeys.PERCENTAGE && targetWeightType === TargetWeights.DRY
            ? targetDryMatterWeight
            : undefined,
        ingredients: ingredientsInRecipe.map((ingredient) => ({
          ...ingredient.mixerData,
          id: ingredient.ownId,
          ingredientId: ingredient.id,
          refusal: ingredient.refusal,
          refusalSubstitute: ingredient.refusalSubstitute,
          amounts: method === EntryMethodsKeys.WEIGHT ? ingredient.actualLoad : undefined,
          loadPercs:
            method === EntryMethodsKeys.PERCENTAGE && targetWeightType === TargetWeights.ACTUAL
              ? ingredient.actualLoadTotalPercentage
              : undefined,
          dryLoadPercs:
            method === EntryMethodsKeys.PERCENTAGE && targetWeightType === TargetWeights.DRY
              ? ingredient.dryMatterTotalPercentage
              : undefined,
        })),
      },
    };
    return newState;
  },
  [updateBaseRationActualLoad.type]: (state: AppRecipeState, action: ReturnType<typeof updateBaseRationActualLoad>) => {
    const { ingredientsInRecipe, baseRation } = state.recipeCreation;
    const newState = {
      recipeCreation: {
        ...state.recipeCreation,
        baseRation: {
          ...baseRation,
          actualLoad: action.payload.baseActualLoad,
        },
      },
      recipePayload: {
        baseRationAmounts: [action.payload.baseActualLoad],
        baseRationId: baseRation.ration!.id,
        ingredients: ingredientsInRecipe.map((ingredient) => ({
          ...ingredient.mixerData,
          id: ingredient.ownId,
          ingredientId: ingredient.id,
          refusal: ingredient.refusal,
          refusalSubstitute: ingredient.refusalSubstitute,
          amounts: ingredient.actualLoad,
        })),
      },
    };
    return newState;
  },
  [updateBaseRationActualLoadPercentage.type]: (
    state: AppRecipeState,
    action: ReturnType<typeof updateBaseRationActualLoadPercentage>,
  ) => {
    const { ingredientsInRecipe, baseRation, targetActualLoadWeight } = state.recipeCreation;
    const newState = {
      recipeCreation: {
        ...state.recipeCreation,
        baseRation: {
          ...baseRation,
          actualLoadTotalPercentage: action.payload.baseActualLoadPercentage,
        },
      },
      recipePayload: {
        baseRationLoadPercs: [action.payload.baseActualLoadPercentage],
        baseRationId: baseRation.ration!.id,
        amounts: targetActualLoadWeight,
        ingredients: ingredientsInRecipe.map((ingredient) => ({
          ...ingredient.mixerData,
          id: ingredient.ownId,
          ingredientId: ingredient.id,
          refusal: ingredient.refusal,
          refusalSubstitute: ingredient.refusalSubstitute,
          loadPercs: ingredient.actualLoadTotalPercentage,
        })),
      },
    };
    return newState;
  },
  [updateBaseRationDryMatter.type]: (state: AppRecipeState, action: ReturnType<typeof updateBaseRationDryMatter>) => {
    const { ingredientsInRecipe, baseRation } = state.recipeCreation;
    const newState = {
      recipeCreation: {
        ...state.recipeCreation,
        baseRation: {
          ...baseRation,
          dryMatter: action.payload.baseDryMatter,
        },
      },
      recipePayload: {
        baseRationDryAmounts: [action.payload.baseDryMatter],
        baseRationId: baseRation.ration!.id,
        ingredients: ingredientsInRecipe.map((ingredient) => {
          const isWater = isWaterIngredient(ingredient.dryMatterPerc);
          return {
            ...ingredient.mixerData,
            id: ingredient.ownId,
            ingredientId: ingredient.id,
            refusal: ingredient.refusal,
            refusalSubstitute: ingredient.refusalSubstitute,
            dryAmounts: isWater ? undefined : ingredient.dryMatter,
            amounts: isWater ? ingredient.actualLoad : undefined,
          };
        }),
      },
    };
    return newState;
  },
  [updateBaseRationDryMatterPercentage.type]: (
    state: AppRecipeState,
    action: ReturnType<typeof updateBaseRationDryMatterPercentage>,
  ) => {
    const { ingredientsInRecipe, baseRation, targetDryMatterWeight } = state.recipeCreation;
    const newState = {
      recipeCreation: {
        ...state.recipeCreation,
        baseRation: {
          ...baseRation,
          dryMatterTotalPercentage: action.payload.baseDryMatterPercentage,
        },
      },
      recipePayload: {
        baseRationDryLoadPercs: [action.payload.baseDryMatterPercentage],
        baseRationId: baseRation.ration!.id,
        dryAmounts: targetDryMatterWeight,
        ingredients: ingredientsInRecipe.map((ingredient) => {
          const isWater = isWaterIngredient(ingredient.dryMatterPerc);
          return {
            ...ingredient.mixerData,
            id: ingredient.ownId,
            ingredientId: ingredient.id,
            refusal: ingredient.refusal,
            refusalSubstitute: ingredient.refusalSubstitute,
            dryLoadPercs: isWater ? undefined : ingredient.dryMatterTotalPercentage,
            loadPercs: isWater ? ingredient.actualLoad : undefined,
          };
        }),
      },
    };
    return newState;
  },
  [setEditRecipe.type]: (state: AppRecipeState, action: ReturnType<typeof setEditRecipe>) => {
    const {
      ingredients,
      amounts,
      dryAmounts,
      baseRationAmounts,
      baseRationDryAmounts,
      baseRationLoadPercs,
      baseRationDryLoadPercs,
      baseRationPerc,
      baseRation,
      rationTypeCode,
      percEntryMethod,
      amountDays,
    } = action.payload;
    const { method } = state.recipeCreation;
    const ingredientsInRecipe = ingredients.map((elem) => ({
      id: elem.ingredientId,
      ownId: elem.id,
      uuid: generateUUID(),
      actualLoad: elem.amounts,
      dryMatter: elem.dryAmounts,
      actualLoadTotalPercentage: elem.loadPercs,
      dryMatterTotalPercentage: elem.dryLoadPercs,
      dryMatterPerc: elem.dryMatterPerc,
      mixerData: {
        mixingTypeCode: elem.mixingTypeCode || 0,
        mixingValue: elem.mixingValue || 0,
        mixingStartTypeCode: elem.mixingStartTypeCode || 1,
        mixingRPM: elem.mixingRPM || 0,
        mixingStartValue: elem.mixingStartValue || 0,
      },
      refusal: elem.refusal,
      refusalSubstitute: elem.refusalSubstitute,
      toleranceMotion: elem.toleranceMotion || 0,
      toleranceWeight: elem.toleranceWeight || 0,
    }));
    const recipeCreation = {
      ...state.recipeCreation,
      targetActualLoadWeight: amounts,
      targetDryMatterWeight: dryAmounts,
      transitionDays: amountDays,
      ingredientsInRecipe,
      recipeTypeId: rationTypeCode,
      baseRation:
        baseRation && Object.keys(baseRation).length !== 0
          ? {
              ration: baseRation,
              ingredients: baseRation.ingredients.map((elem) => ({
                id: elem.ingredientId,
                ownId: elem.id,
                uuid: elem.id,
                actualLoad: elem.amounts,
                dryMatter: elem.dryAmounts,
                actualLoadTotalPercentage: elem.loadPercs,
                dryMatterTotalPercentage: elem.dryLoadPercs,
                dryMatterPerc: elem.dryMatterPerc,
                mixerData: {
                  mixingTypeCode: elem.mixingTypeCode || 0,
                  mixingValue: elem.mixingValue || 0,
                  mixingStartTypeCode: elem.mixingStartTypeCode || 1,
                  mixingRPM: elem.mixingRPM || 0,
                  mixingStartValue: elem.mixingStartValue || 0,
                },
                refusal: elem.refusal,
                refusalSubstitute: elem.refusalSubstitute,
              })),
              percentage: baseRationPerc,
              actualLoad: baseRationAmounts ? baseRationAmounts[0] : 0,
              dryMatter: baseRationDryAmounts ? baseRationDryAmounts[0] : 0,
              actualLoadTotalPercentage: baseRationLoadPercs ? baseRationLoadPercs[0] : 0,
              dryMatterTotalPercentage: baseRationDryLoadPercs ? baseRationDryLoadPercs[0] : 0,
            }
          : state.recipeCreation.baseRation,
      previewRecipe: action.payload,
    };
    const recipePayload = {
      ...state.recipePayload,
      baseRationId: baseRation?.id,
      amounts:
        method === EntryMethodsKeys.PERCENTAGE && percEntryMethod === TargetWeights.ACTUAL && amounts
          ? amounts
          : undefined,
      dryAmounts:
        method === EntryMethodsKeys.PERCENTAGE && percEntryMethod === TargetWeights.DRY && dryAmounts
          ? dryAmounts
          : undefined,
      baseRationAmounts: baseRation?.baseRationAmounts ? [baseRation.baseRationAmounts[0]] : undefined,
      ingredients: ingredientsInRecipe.map((ingredient) => ({
        id: ingredient.ownId,
        ingredientId: ingredient.id,
        amounts: method === EntryMethodsKeys.WEIGHT ? ingredient.actualLoad : undefined,
        loadPercs:
          method === EntryMethodsKeys.PERCENTAGE &&
          (percEntryMethod === TargetWeights.ACTUAL ||
            (percEntryMethod === TargetWeights.DRY && isWaterIngredient(ingredient.dryMatterPerc!)))
            ? ingredient.actualLoadTotalPercentage
            : undefined,
        dryLoadPercs:
          method === EntryMethodsKeys.PERCENTAGE &&
          percEntryMethod === TargetWeights.DRY &&
          !isWaterIngredient(ingredient.dryMatterPerc!)
            ? ingredient.dryMatterTotalPercentage
            : undefined,
        refusal: ingredient.refusal,
        refusalSubstitute: ingredient.refusalSubstitute,
      })),
    };
    return {
      recipeCreation,
      recipePayload,
    };
  },
  [setWaterAmount.type]: (state: AppRecipeState, action: Action<number>) => {
    const { method, targetWeightType } = state.recipeCreation;
    return dotProp.set(state, 'recipePayload', {
      ...state.recipePayload,
      ingredients: state.recipeCreation.ingredientsInRecipe.map((ingredient) => {
        const isWater = isWaterIngredient(ingredient.dryMatterPerc);
        return {
          ...ingredient.mixerData,
          id: ingredient.ownId,
          ingredientId: ingredient.id,
          refusal: ingredient.refusal,
          refusalSubstitute: ingredient.refusalSubstitute,
          amounts: method === EntryMethodsKeys.WEIGHT ? ingredient.actualLoad : undefined,
          loadPercs:
            method === EntryMethodsKeys.PERCENTAGE && (targetWeightType === TargetWeights.ACTUAL || isWater)
              ? ingredient.actualLoadTotalPercentage
              : undefined,
          dryLoadPercs:
            method === EntryMethodsKeys.PERCENTAGE && targetWeightType === TargetWeights.DRY
              ? ingredient.dryMatterTotalPercentage
              : undefined,
        };
      }),
      targetDMPerc: action.payload,
    });
  },
  [setActualLoadTargetWeight.type]: (state: AppRecipeState, action: Action<typeof setActualLoadTargetWeight>) => {
    const { payload } = action;
    const { ingredientsInRecipe, recipeTypeId, baseRation, currentTransitionDay, targetActualLoadWeight } =
      state.recipeCreation;
    const isBaseRation = isBaseRationType(recipeTypeId);
    const targetWeight = targetActualLoadWeight.map((weight, index) =>
      index === currentTransitionDay ? payload : weight,
    );
    const newState = {
      recipeCreation: {
        ...state.recipeCreation,
        targetActualLoadWeight: targetWeight,
        targetWeightType: TargetWeights.ACTUAL,
      },
      recipePayload: {
        ...state.recipePayload,
        baseRationLoadPercs: isBaseRation ? [baseRation.actualLoadTotalPercentage] : undefined,
        baseRationId: isBaseRation && baseRation.ration ? baseRation.ration.id : undefined,
        amounts: targetWeight,
        ingredients: ingredientsInRecipe.map((ingredient) => ({
          ...ingredient.mixerData,
          id: ingredient.ownId,
          ingredientId: ingredient.id,
          refusal: ingredient.refusal,
          refusalSubstitute: ingredient.refusalSubstitute,
          loadPercs: ingredient.actualLoadTotalPercentage,
        })),
      },
    };
    return newState;
  },
  [setDryMatterTargetWeight.type]: (state: AppRecipeState, action: Action<typeof setDryMatterTargetWeight>) => {
    const { payload } = action;
    const { ingredientsInRecipe, recipeTypeId, baseRation, currentTransitionDay, targetDryMatterWeight } =
      state.recipeCreation;
    const isBaseRation = isBaseRationType(recipeTypeId);
    const targetWeight = targetDryMatterWeight.map((weight, index) =>
      index === currentTransitionDay ? payload : weight,
    );
    const newState = {
      recipeCreation: {
        ...state.recipeCreation,
        targetDryMatterWeight: targetWeight,
        targetWeightType: TargetWeights.DRY,
      },
      recipePayload: {
        ...state.recipePayload,
        baseRationDryLoadPercs: isBaseRation ? [baseRation.dryMatterTotalPercentage] : undefined,
        baseRationId: isBaseRation && baseRation.ration ? baseRation.ration.id : undefined,
        dryAmounts: targetWeight,
        ingredients: ingredientsInRecipe.map((ingredient) => {
          const isWater = isWaterIngredient(ingredient.dryMatterPerc);
          return {
            ...ingredient.mixerData,
            id: ingredient.ownId,
            ingredientId: ingredient.id,
            refusal: ingredient.refusal,
            refusalSubstitute: ingredient.refusalSubstitute,
            loadPercs: isWater ? ingredient.actualLoadTotalPercentage : undefined,
            dryLoadPercs: !isWater ? ingredient.dryMatterTotalPercentage : undefined,
          };
        }),
      },
    };
    return newState;
  },
  [setSelectedIngredientInList.type]: (state: AppRecipeState, action: Action<string | null>) => {
    return dotProp.set(state, 'recipeCreation.selectedIngredientInList', action.payload);
  },
  [addIngredientInRecipe.type]: addIngredientInRecipeReducer,
  [addIngredientInRecipeSilent.type]: addIngredientInRecipeReducer,
  [updateIngredientOrderInRecipe.type]: (
    state: AppRecipeState,
    action: Action<{ newPosition: number; uuid: string }>,
  ) => {
    const movedObject = action.payload;
    const currentArray = state.recipeCreation.ingredientsInRecipe;
    const currentItem = currentArray.find((ingredient) => ingredient.uuid === movedObject.uuid);
    const findIndex = currentArray.findIndex((currentIndex) => currentIndex.uuid === movedObject.uuid);
    const slicedArray = remove(findIndex, 1, currentArray);
    const newArray = insert(movedObject.newPosition, currentItem, slicedArray);
    return dotProp.set(state, 'recipeCreation.ingredientsInRecipe', newArray);
  },
  [updateBaseRationRecipe.done.type]: (
    state: AppRecipeState,
    action: ReturnType<typeof updateBaseRationRecipe.done>,
  ) => {
    const recipe = action.payload.result;
    const { baseRation, method, targetActualLoadWeight, targetDryMatterWeight, targetWeightType, ingredientsInRecipe } =
      state.recipeCreation;
    const perc = state.recipeCreation.baseRation.percentage;
    const newState = {
      recipeCreation: {
        ...state.recipeCreation,
        baseRation: {
          ...baseRation,
          ration: recipe,
          percentage: perc,
        },
      },
      recipePayload: {
        ...state.recipePayload,
        baseRationPerc: method === EntryMethodsKeys.PERCENTAGE ? undefined : !baseRation.ration ? 100 : perc,
        baseRationId: recipe.id,
        amounts:
          method === EntryMethodsKeys.PERCENTAGE && targetWeightType === TargetWeights.ACTUAL
            ? targetActualLoadWeight
            : undefined,
        dryAmounts:
          method === EntryMethodsKeys.PERCENTAGE && targetWeightType === TargetWeights.DRY
            ? targetDryMatterWeight
            : undefined,
        ingredients: ingredientsInRecipe.map((ingredient) => {
          const isWater = isWaterIngredient(ingredient.dryMatterPerc);
          const isDryLoadPerc = method === EntryMethodsKeys.PERCENTAGE && targetWeightType === TargetWeights.DRY;
          const isActualLoadPerc = method === EntryMethodsKeys.PERCENTAGE && targetWeightType === TargetWeights.ACTUAL;
          return {
            ...ingredient.mixerData,
            id: ingredient.ownId,
            ingredientId: ingredient.id,
            refusal: ingredient.refusal,
            refusalSubstitute: ingredient.refusalSubstitute,
            amounts: method === EntryMethodsKeys.WEIGHT ? ingredient.actualLoad : undefined,
            loadPercs: isActualLoadPerc || isWater ? ingredient.actualLoadTotalPercentage : undefined,
            dryLoadPercs: isDryLoadPerc ? ingredient.dryMatterTotalPercentage : undefined,
          };
        }),
      },
    };
    return newState;
  },
  [deleteIngredientInRecipe.type]: (state: AppRecipeState, action: Action<{ id: string }>) => {
    const { ingredientsInRecipe, method, targetWeightType } = state.recipeCreation;
    const newIngredients = ingredientsInRecipe.filter((ingredient) => ingredient.uuid !== action.payload.id);

    const newState = {
      recipeCreation: {
        ...state.recipeCreation,
        ingredientsInRecipe: newIngredients,
      },
      recipePayload: {
        ...state.recipePayload,
        ingredients: newIngredients.map((ingredient) => ({
          ...ingredient.mixerData,
          id: ingredient.ownId,
          ingredientId: ingredient.id,
          refusal: ingredient.refusal,
          refusalSubstitute: ingredient.refusalSubstitute,
          amounts: method === EntryMethodsKeys.WEIGHT ? ingredient.actualLoad : undefined,
          loadPercs:
            method === EntryMethodsKeys.PERCENTAGE &&
            (targetWeightType === TargetWeights.ACTUAL || isWaterIngredient(ingredient.dryMatterPerc))
              ? ingredient.actualLoadTotalPercentage
              : undefined,
          dryLoadPercs:
            method === EntryMethodsKeys.PERCENTAGE &&
            targetWeightType === TargetWeights.DRY &&
            !isWaterIngredient(ingredient.dryMatterPerc)
              ? ingredient.dryMatterTotalPercentage
              : undefined,
        })),
      },
    };

    return newState;
  },
  [emptyIngredientsInRecipe.type]: (state: AppRecipeState) =>
    ({
      ...state,
      recipeCreation: {
        ...state.recipeCreation,
        ingredientsInRecipe: [],
        isEditMode: state.recipeCreation.isEditMode,
        selectedIngredientInList: state.recipeCreation.selectedIngredientInList,
        currentTransitionDay: 0,
        transitionDays: [0],
        baseRation: {
          ration: null,
          percentage: 100,
          actualLoad: 0,
          dryMatter: 0,
          actualLoadTotalPercentage: 0,
          dryMatterTotalPercentage: 0,
          ingredients: [],
        },
        autoAdjWater: false,
        previewRecipe: null,
        targetActualLoadWeight: [0],
        targetDryMatterWeight: [0],
        finalDryMatterPercentage: 0,
        recipeTypeId: recipeTypeItems[RecipeType.Default].id,
      },
      recipePayload: {
        ingredients: [],
      },
    } as AppRecipeState),
  [previewCreateRecipe.done.type]: (state: AppRecipeState, action: Action<{ result: PreviewRationCreation }>) => {
    const {
      ingredients,
      amounts,
      dryAmounts,
      baseRationAmounts,
      baseRationDryAmounts,
      baseRationLoadPercs,
      baseRationDryLoadPercs,
      baseRationPerc,
      baseRationId,
      finalDateBeforeTransition,
    } = action.payload.result;
    const { baseRation } = state.recipeCreation;
    const recipeCreation = {
      ...state.recipeCreation,
      finalDateBeforeTransition,
      targetActualLoadWeight: amounts,
      targetDryMatterWeight: dryAmounts,
      ingredientsInRecipe: state.recipeCreation.ingredientsInRecipe.map((ingredient, index) => ({
        ...ingredient,
        actualLoad: ingredients && ingredients[index]?.amounts ? ingredients[index].amounts : [0],
        dryMatter: ingredients && ingredients[index]?.dryAmounts ? ingredients[index].dryAmounts : [0],
        actualLoadTotalPercentage: ingredients && ingredients[index]?.loadPercs ? ingredients[index].loadPercs : [0],
        dryMatterTotalPercentage:
          ingredients && ingredients[index]?.dryLoadPercs ? ingredients[index].dryLoadPercs : [0],
        dryMatterPerc: state.recipeCreation.ingredientsInRecipe[index]?.dryMatterPerc,
        mixerData: {
          mixingValue: ingredients && ingredients[index]?.mixingValue,
          mixingTypeCode: ingredients && ingredients[index]?.mixingTypeCode,
          mixingStartTypeCode: ingredients && ingredients[index]?.mixingStartTypeCode,
          mixingRPM: ingredients && ingredients[index]?.mixingRPM,
          mixingStartValue: ingredients && ingredients[index]?.mixingStartValue,
        },
      })),
      baseRation: {
        ...baseRation,
        percentage: baseRationId ? baseRationPerc : baseRation.percentage,
        actualLoad: baseRationAmounts ? baseRationAmounts[0] : 0,
        dryMatter: baseRationDryAmounts ? baseRationDryAmounts[0] : 0,
        actualLoadTotalPercentage: baseRationLoadPercs ? baseRationLoadPercs[0] : 0,
        dryMatterTotalPercentage: baseRationDryLoadPercs ? baseRationDryLoadPercs[0] : 0,
        ingredients: recalculateBaseRationIngredients(
          baseRation.ration,
          baseRationPerc || 0,
          baseRationAmounts ? baseRationAmounts[0] : 0,
          baseRationDryAmounts ? baseRationDryAmounts[0] : 0,
        ),
      },
      previewRecipe: action.payload.result,
    };
    return dotProp.set(state, 'recipeCreation', recipeCreation);
  },
  [previewCreateRecipe.failed.type]: (state: AppRecipeState, action: Action<any>) => {
    return dotProp.set(state, 'recipeCreation.previewRecipe', null);
  },
  [updateRecipeTypeId.type]: (state: AppRecipeState, action: ReturnType<typeof updateRecipeTypeId>) => {
    const recipeTypeId = action.payload;
    const totalActualLoad = calcTotalActualLoad(state, recipeTypeId);
    const totalDryMatter = calcTotalDryMatter(state, recipeTypeId);
    const isIngredientResize = isIngredientResizeType(recipeTypeId);
    const isDefault = isDefaultType(recipeTypeId);
    const isRefusal = isRefusalType(recipeTypeId);
    const isBaseRation = isBaseRationType(recipeTypeId);

    const ingredientsFiltered = state.recipeCreation.ingredientsInRecipe.filter((x) => x.id !== '-1' && !x.refusal);

    const ingredients = (() => {
      if (isIngredientResize) {
        if (ingredientsFiltered.length === 0) {
          return [
            {
              id: '-1',
              uuid: generateUUID(),
              actualLoad: [0],
              dryMatter: [0],
              actualLoadTotalPercentage: [0],
              dryMatterTotalPercentage: [0],
              mixerData: {
                mixingStartTypeCode: 1,
                mixingTypeCode: 0,
                mixingValue: 0,
              },
              dryMatterPerc: 0,
              refusal: false,
              refusalSubstitute: false,
              toleranceMotion: 0,
              toleranceWeight: 0,
            },
          ];
        } else {
          return ingredientsFiltered;
        }
      } else if (isDefault) {
        return ingredientsFiltered;
      } else if (isRefusal) {
        const refusalIngredients = [
          {
            id: '-1',
            ownId: '-1',
            uuid: generateUUID(),
            actualLoad: [0],
            dryMatter: [0],
            actualLoadTotalPercentage: [0],
            dryMatterTotalPercentage: [0],
            mixerData: {
              mixingStartTypeCode: 1,
              mixingTypeCode: 0,
              mixingValue: 0,
            },
            dryMatterPerc: 0,
            refusal: true,
            refusalSubstitute: true,
            toleranceMotion: 0,
            toleranceWeight: 0,
          },
        ];
        const otherIngredients = ingredientsFiltered.filter((x) => !x.refusal);

        return [...refusalIngredients, ...otherIngredients];
      }
      return ingredientsFiltered;
    })();

    return {
      ...state,
      recipeCreation: {
        ...state.recipeCreation,
        transitionDays: isDefault || isIngredientResize ? state.recipeCreation.transitionDays : [0],
        currentTransitionDay: isDefault || isIngredientResize ? state.recipeCreation.currentTransitionDay : 0,
        ingredientsInRecipe: ingredients.map((ing) => ({
          ...ing,
          uuid: generateUUID(),
          actualLoad: [ing.actualLoad[0]],
          dryMatter: [ing.dryMatter[0]],
          actualLoadTotalPercentage: [(ing.actualLoad[0] / totalActualLoad) * 100 || 0],
          dryMatterTotalPercentage: [(ing.dryMatter[0] / totalDryMatter) * 100 || 0],
        })),
        baseRation:
          recipeTypeId === recipeTypeItems[RecipeType.AddToBaseRecipe].id
            ? state.recipeCreation.baseRation
            : {
                ration: null,
                percentage: 100,
                actualLoad: 0,
                dryMatter: 0,
                actualLoadTotalPercentage: 0,
                dryMatterTotalPercentage: 0,
                ingredients: [],
              },
        recipeTypeId,
      },
      recipePayload: {
        ...state.recipePayload,
        amountDays: isDefault || isIngredientResize ? state.recipeCreation.transitionDays : [0],
        baseRationPerc: isBaseRation ? state.recipeCreation.baseRation.percentage : undefined,
        ingredients: ingredients.map((x) => ({
          ...x.mixerData,
          id: x.ownId,
          ingredientId: x.id,
          refusal: !!x.refusal,
          refusalSubstitute: x.refusalSubstitute,
          amounts: x.actualLoad,
        })),
      },
    };
  },
  [addRecipeTransitionDays.type]: (state: AppRecipeState, action: ReturnType<typeof addRecipeTransitionDays>) => {
    const { recipeCreation } = state;
    const { ingredientsInRecipe, transitionDays, targetActualLoadWeight, targetDryMatterWeight } = recipeCreation;
    const days = action.payload;
    let index = transitionDays.findIndex((elem, index) => {
      if (index + 1 >= transitionDays.length) {
        return index;
      }
      return elem <= days && days <= transitionDays[index + 1];
    });

    if (index < 0) index++;

    const newTransitionDays = Array.from(transitionDays);
    newTransitionDays.splice(index + 1, 0, days);
    const amounts = targetActualLoadWeight.slice(0, index + 1).concat(targetActualLoadWeight.slice(index));
    const dryAmounts = targetDryMatterWeight.slice(0, index + 1).concat(targetDryMatterWeight.slice(index));

    const ingredients = ingredientsInRecipe.map((ing) => ({
      ...ing,
      actualLoad: ing.actualLoad.slice(0, index + 1).concat(ing.actualLoad.slice(index)),
      dryMatter: ing.dryMatter.slice(0, index + 1).concat(ing.dryMatter.slice(index)),
      actualLoadTotalPercentage: ing.actualLoadTotalPercentage
        .slice(0, index + 1)
        .concat(ing.actualLoadTotalPercentage.slice(index)),
      dryMatterTotalPercentage: ing.dryMatterTotalPercentage
        .slice(0, index + 1)
        .concat(ing.dryMatterTotalPercentage.slice(index)),
    }));
    return {
      ...state,
      recipeCreation: {
        ...recipeCreation,
        transitionDays: newTransitionDays,
        currentTransitionDay: index + 1,
        ingredientsInRecipe: ingredients,
        targetActualLoadWeight: amounts,
        targetDryMatterWeight: dryAmounts,
      },
      recipePayload: {
        ...state.recipePayload,
        amountDays: newTransitionDays,
        amounts,
        dryAmounts,
        ingredients: ingredients.map((elem) => ({
          ...elem.mixerData,
          id: elem.ownId,
          ingredientId: elem.id,
          amounts: elem.actualLoad,
          dryAmounts: elem.dryMatter,
          loadPercs: elem.actualLoadTotalPercentage,
          dryLoadPercs: elem.dryMatterTotalPercentage,
          refusal: !!elem.refusal,
          refusalSubstitute: !!elem.refusalSubstitute,
        })),
      },
    };
  },
  [editCurrentTransitionDay.type]: (state: AppRecipeState, action: ReturnType<typeof editCurrentTransitionDay>) => {
    const { transitionDays, currentTransitionDay } = state.recipeCreation;
    const newTransitionDays = transitionDays
      .map((elem, index) => (index === currentTransitionDay ? action.payload : elem))
      .sort((a, b) => a - b);

    const newCurrentTransitionDay = newTransitionDays.findIndex((day) => day === action.payload);

    return {
      recipeCreation: {
        ...state.recipeCreation,
        transitionDays: newTransitionDays,
        currentTransitionDay: newCurrentTransitionDay,
      },
      recipePayload: {
        ...state.recipePayload,
        amountDays: newTransitionDays,
      },
    };
  },
  [deleteCurrentTransionDay.type]: (state: AppRecipeState, action: ReturnType<typeof deleteCurrentTransionDay>) => {
    const { ingredientsInRecipe, currentTransitionDay, transitionDays, targetActualLoadWeight, targetDryMatterWeight } =
      state.recipeCreation;
    const ingredients = ingredientsInRecipe.map((ing) => ({
      ...ing,
      actualLoad: ing.actualLoad.filter((_, index) => currentTransitionDay !== index),
      dryMatter: ing.dryMatter.filter((_, index) => currentTransitionDay !== index),
      actualLoadTotalPercentage: ing.actualLoadTotalPercentage.filter((_, index) => currentTransitionDay !== index),
      dryMatterTotalPercentage: ing.dryMatterTotalPercentage.filter((_, index) => currentTransitionDay !== index),
    }));
    const amountDays = transitionDays.filter((_, index) => currentTransitionDay !== index);
    const totalAmounts = targetActualLoadWeight.filter((_, index) => currentTransitionDay !== index);
    const totalDryAmounts = targetDryMatterWeight.filter((_, index) => currentTransitionDay !== index);
    return {
      ...state,
      recipeCreation: {
        ...state.recipeCreation,
        transitionDays: amountDays,
        ingredientsInRecipe: ingredients,
        targetActualLoadWeight: totalAmounts,
        targetDryMatterWeight: totalDryAmounts,
        currentTransitionDay: currentTransitionDay - 1,
      },
      recipePayload: {
        ...state.recipePayload,
        amounts: totalAmounts,
        dryAmounts: totalDryAmounts,
        amountDays,
        ingredients: ingredients.map((ing) => ({
          ...ing.mixerData,
          id: ing.ownId,
          ingredientId: ing.id,
          amounts: ing.actualLoad,
          dryAmounts: ing.dryMatter,
          loadPercs: ing.actualLoadTotalPercentage,
          dryLoadPercs: ing.dryMatterTotalPercentage,
          refusal: !!ing.refusal,
          refusalSubstitute: !!ing.refusalSubstitute,
        })),
      },
    };
  },
  [changeIngredient.done.type]: (state: AppRecipeState, action: ReturnType<typeof changeIngredient.done>) => {
    const changedIngredient = action.payload.result;
    const { method } = state.recipeCreation;
    return {
      ...state,
      recipeCreation: {
        ...state.recipeCreation,
        ingredientsInRecipe: state.recipeCreation.ingredientsInRecipe.map((x) => {
          return x.uuid === changedIngredient.uuid ? { ...x, ...changedIngredient } : x;
        }),
      },
      recipePayload: {
        ...state.recipePayload,
        ingredients: state.recipeCreation.ingredientsInRecipe.map((x) => {
          return x.uuid === changedIngredient.uuid
            ? {
                ...x.mixerData,
                id: changedIngredient.ownId,
                amounts: method === EntryMethodsKeys.WEIGHT ? changedIngredient?.actualLoad : undefined,
                loadPercs:
                  method === EntryMethodsKeys.PERCENTAGE ? changedIngredient?.actualLoadTotalPercentage : undefined,
                refusalSubstitute: changedIngredient.refusalSubstitute,
                refusal: changedIngredient.refusal,
                ingredientId: changedIngredient.id,
              }
            : {
                ...x.mixerData,
                id: x.ownId,
                amounts: method === EntryMethodsKeys.WEIGHT ? x?.actualLoad : undefined,
                loadPercs: method === EntryMethodsKeys.PERCENTAGE ? x?.actualLoadTotalPercentage : undefined,
                refusal: x.refusal,
                refusalSubstitute: x.refusalSubstitute,
                ingredientId: x.id,
              };
        }),
      },
    };
  },
  [updateRefusalSubstitute.type]: (state: AppRecipeState, action: ReturnType<typeof updateRefusalSubstitute>) => {
    const { recipePayload, recipeCreation } = state;
    const { method, targetWeightType } = recipeCreation;
    const newState = {
      recipeCreation: {
        ...recipeCreation,
        ingredientsInRecipe: recipeCreation.ingredientsInRecipe.map((x) => {
          const updatedIng = action.payload.find((updIng) => updIng.uuid === x.uuid);
          return updatedIng ? { ...x, refusalSubstitute: updatedIng.refusalSubstitute } : x;
        }),
      },
      recipePayload: {
        ...recipePayload,
        ingredients: recipeCreation.ingredientsInRecipe.map((ingredient) => {
          const updatedIng = action.payload.find((updIng) => updIng.uuid === ingredient.uuid);
          const ing = {
            ...ingredient.mixerData,
            id: ingredient.ownId,
            ingredientId: ingredient.id,
            refusal: ingredient.refusal,
            refusalSubstitute: ingredient.refusalSubstitute,
            amounts: method === EntryMethodsKeys.WEIGHT ? ingredient.actualLoad : undefined,
            loadPercs:
              method === EntryMethodsKeys.PERCENTAGE &&
              (targetWeightType === TargetWeights.ACTUAL ||
                (targetWeightType === TargetWeights.DRY && isWaterIngredient(ingredient.dryMatterPerc!)))
                ? ingredient.actualLoadTotalPercentage
                : undefined,
            dryLoadPercs:
              method === EntryMethodsKeys.PERCENTAGE &&
              targetWeightType === TargetWeights.DRY &&
              !isWaterIngredient(ingredient.dryMatterPerc!)
                ? ingredient.dryMatterTotalPercentage
                : undefined,
          };
          return updatedIng
            ? {
                ...ing,
                refusalSubstitute: updatedIng.refusalSubstitute,
              }
            : ing;
        }),
      },
    };
    return newState;
  },
});

const calcTotalActualLoad = (state: AppRecipeState, recipeTypeId: number): number => {
  const isBaseRation = isBaseRationType(recipeTypeId);
  let addandActualLoad = 0;
  if (isBaseRation) {
    addandActualLoad = state.recipeCreation.baseRation.actualLoad;
  }
  const { currentTransitionDay } = state.recipeCreation;
  return (
    state.recipeCreation.ingredientsInRecipe.reduce(
      (accum, ing) => (accum += ing.actualLoad[currentTransitionDay]),
      0,
    ) + addandActualLoad
  );
};

const calcTotalDryMatter = (state: AppRecipeState, recipeTypeId: number): number => {
  const isBaseRation = isBaseRationType(recipeTypeId);
  let addandDryMatter = 0;
  if (isBaseRation) {
    addandDryMatter = state.recipeCreation.baseRation.dryMatter;
  }
  const { currentTransitionDay } = state.recipeCreation;
  return (
    state.recipeCreation.ingredientsInRecipe.reduce((accum, ing) => (accum += ing.dryMatter[currentTransitionDay]), 0) +
    addandDryMatter
  );
};

const recalculateBaseRationIngredients = (
  recipe: Recipe | null | undefined,
  percentage: number,
  actualLoad: number,
  dryMatter: number,
): IngredientInRecipeCreation[] => {
  const changedActualLoadFieldValue = Number(actualLoad);
  const changedDryMatterFieldValue = Number(dryMatter);

  const ingredients = recipe
    ? recipe.ingredients.map((ing) => {
        const al = ing.amounts[0] * (percentage / 100) || 0;
        const dm = ing.dryAmounts[0] * (percentage / 100) || 0;
        return {
          id: ing.ingredientId,
          uuid: ing.id,
          dryMatterPerc: ing.dryMatterPerc || 0,
          actualLoad: [Number(al)],
          dryMatter: [Number(dm)],
          actualLoadTotalPercentage: [(al / changedActualLoadFieldValue) * 100 || 0],
          dryMatterTotalPercentage: [(dm / changedDryMatterFieldValue) * 100 || 0],
          mixerData: {
            mixingStartTypeCode: ing.mixingStartTypeCode || 1,
            mixingTypeCode: ing.mixingTypeCode || 0,
            mixingValue: ing.mixingValue || 0,
            mixingRPM: ing.mixingRPM || 0,
            mixingStartValue: ing.mixingStartValue || 0,
          },
          toleranceMotion: ing.toleranceMotion || 0,
          toleranceWeight: ing.toleranceWeight || 0,
        };
      })
    : [];

  return ingredients;
};

const isDefaultType = (id: number) => id === recipeTypeItems[RecipeType.Default].id;
const isIngredientResizeType = (id: number) => id === recipeTypeItems[RecipeType.IngredientResize].id;
const isBaseRationType = (id: number) => id === recipeTypeItems[RecipeType.AddToBaseRecipe].id;
const isRefusalType = (id: number) => id === recipeTypeItems[RecipeType.RefusalSubstitute].id;
const isWaterIngredient = (dryMatterPerc: number) => dryMatterPerc === 0;

export default appRecipeReducer;
